import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useRef } from "react";
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setTokenExpired } from '../../Redux/authSlice';
import { useScreenTracking } from "../../hooks/useScreenTracking";
import { trackMarketplaceLogin } from "../../utils/gtm";
import { getJwtExpirationDate, getCookie, removeCookie, setCookie } from "../../lib/cookies";
import {
  getWhitelabelFromMemory,
  removeCustomerFromMemory,
  setCustomerInMemory,
  setWhitelabelInMemory,
} from "../../lib/sharedSessionMemory";

const LOADING_GIF_URL = process.env.REACT_APP_LOADING_GIF_URL || "https://i.sstatic.net/kOnzy.gif";


const createLoaderInDOM = () => {
  const existingLoader = document.getElementById('auth-loader-container');
  if (existingLoader) {
    return existingLoader;
  }

  const loaderContainer = document.createElement('div');
  loaderContainer.id = 'auth-loader-container';
  loaderContainer.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: #f7f7f7;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    overflow: hidden;
  `;

  const preloader = document.createElement('div');
  preloader.style.cssText = `
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  `;

  const preloaderInner = document.createElement('div');
  preloaderInner.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const loaderImage = document.createElement('img');
  loaderImage.src = LOADING_GIF_URL;
  loaderImage.alt = 'Carregando...';
  loaderImage.style.cssText = `
    max-width: 200px;
    max-height: 200px;
    width: auto;
    height: auto;
  `;

  preloaderInner.appendChild(loaderImage);
  preloader.appendChild(preloaderInner);
  loaderContainer.appendChild(preloader);
  document.body.appendChild(loaderContainer);

  document.body.style.overflow = 'hidden';

  return loaderContainer;
};

const removeLoaderFromDOM = () => {
  const loader = document.getElementById('auth-loader-container');
  if (loader) {
    loader.remove();
  }
  document.body.style.overflow = '';
};

if (typeof document !== 'undefined') {
  if (window.location.pathname.includes('/auth') || window.location.search.includes('token=')) {
    if (document.body) {
      createLoaderInDOM();
    } else {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          createLoaderInDOM();
        });
      } else {
        createLoaderInDOM();
      }
    }
  }
}

const fetchWhitelabelOnce = async (whitelabelUrl, apiToken, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await axios.get(whitelabelUrl, {
      headers: { Authorization: `Bearer ${apiToken}` },
      signal: controller.signal,
      timeout,
    });
    clearTimeout(timeoutId);
    if (response?.data?.data) return response.data.data;
    throw new Error('Nenhum dado de whitelabel retornado na resposta');
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export default function AuthRedirectComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authFlowStarted = useRef(false);
  const screenName = useScreenTracking();

  useEffect(() => {
    createLoaderInDOM();

    const runAuthFlow = async () => {
      if (authFlowStarted.current) return;
      authFlowStarted.current = true;
      try {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        const redirect = queryParams.get('redirect');
        
        if (!token) {
          removeLoaderFromDOM();
          window.location.href = '/';
          return;
        }

        const wlTokenExpiration = getJwtExpirationDate(token);
        setCookie('wlt', token, wlTokenExpiration ? { expiresAt: wlTokenExpiration } : {});
        setCustomerInMemory({});

        if (redirect && redirect.trim()) {
          localStorage.setItem('redirect_path', redirect.trim());
        }

        const API_URL_CONFIG = process.env.REACT_APP_CONFIGS_API;
        const API_TOKEN_CONFIG = process.env.REACT_APP_CONFIGS_API_TOKEN;
        const API_URL_CREDENCIAIS = `${process.env.REACT_APP_API_AUTHORIZATION}/v1`;
        const API_CHANNEL = process.env.REACT_APP_CONFIGS_CHANNEL;
        const API_MONOLITO = process.env.REACT_APP_MONOLITO_URL;

        let channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          channel = payload?.channel ?? channel;
        } catch (err) {
          console.warn('Falha ao decodificar token:', err);
        }

        const { data: authData } = await axios.post(
          `${API_URL_CREDENCIAIS}/jwt/validate-channel`,
          { channel, token }
        );

        if (!authData?.success) {
          console.error('Validação de canal falhou');
          removeCookie('wlt');
          removeCustomerFromMemory();
          removeLoaderFromDOM();
          window.location.href = '/';
          return;
        }

        setCustomerInMemory(authData);
        dispatch(setTokenExpired(false));

        const entrypointUrl = `${API_MONOLITO}/${channel}/entrypoint?token=${encodeURIComponent(token)}`;
        const whitelabelUrl = `${API_URL_CONFIG}/whitelabels/byChannel/${API_CHANNEL}`;

        let whitelabelData = null;
        try {
          whitelabelData = await fetchWhitelabelOnce(whitelabelUrl, API_TOKEN_CONFIG);
          
          if (whitelabelData) {
            setWhitelabelInMemory(whitelabelData);
          }
        } catch (whitelabelError) {
          const cachedWhitelabel = getWhitelabelFromMemory();
          if (cachedWhitelabel) {
            whitelabelData = cachedWhitelabel;
          } else {
            removeLoaderFromDOM();
            console.error('Erro ao carregar configurações. Por favor, tente novamente.');
            window.location.href = '/';
            return;
          }
        }

        try {
          await axios.get(entrypointUrl, { 
            withCredentials: true,
            timeout: 10000 
          });
        } catch (entrypointError) {
          console.error('Erro ao criar sessão:', entrypointError.message);
        }

        if (!whitelabelData) {
          removeLoaderFromDOM();
          window.location.href = '/';
          return;
        }

        const redirectPath = localStorage.getItem('redirect_path');
        trackMarketplaceLogin({ screenName });
        removeLoaderFromDOM();
        navigate(redirectPath || '/', { replace: true });
      } catch (err) {
        console.error('Erro no processo de autenticação:', err);
        removeCookie('wlt');
        removeCustomerFromMemory();
        removeLoaderFromDOM();
        window.location.href = '/';
      }
    };

    runAuthFlow();
    return () => {
    };
  }, [location.search]);


  return null;
}
